import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalOverflow from '@mui/joy/ModalOverflow';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Checkbox from '@mui/joy/Checkbox';
import CircularProgress from '@mui/joy/CircularProgress';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import { MOTION, ellipsis, iconColor, thinScrollbar } from './chatTheme';

const EMOJI_SET = ['', '📁', '💼', '📐', '📏', '🧑‍💼', '🏠', '⭐', '🔥', '✅', '💬', '📞', '🎫'];

/**
 * Папки чатов: список, создание, правка — и режим «положить чат в папку».
 *
 * Два состояния в одном окне:
 *  • mode='list'  — все папки, кнопки правки и удаления;
 *  • mode='form'  — форма одной папки;
 *  • mode='pick'  — открыто из меню чата: отметить, в каких папках он лежит.
 *
 * Папка может быть ручной, умной (набирается по признакам — «чаты с
 * обмерщиками», «чаты с клиентами») или и той и другой сразу: состав
 * считается на сервере при выдаче, ничего заранее не раскладывается.
 */
export default function FolderModal({
  open,
  folders = [],
  rules = [],
  pickChat = null,        // чат, если окно открыто пунктом «Добавить в папку»
  busy = false,
  onCreate,
  onUpdate,
  onDelete,
  onToggleChat,
  onClose,
}) {
  const [mode, setMode] = useState('list');
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode(pickChat ? 'pick' : 'list');
    setEditing(null);
    setName('');
    setEmoji('');
    setPicked([]);
    setError('');
  }, [open, pickChat]);

  const startCreate = () => {
    setEditing(null);
    setName('');
    setEmoji('');
    setPicked([]);
    setError('');
    setMode('form');
  };

  const startEdit = (folder) => {
    setEditing(folder);
    setName(folder.name || '');
    setEmoji(folder.emoji || '');
    setPicked(folder.auto_rules || []);
    setError('');
    setMode('form');
  };

  const toggleRule = (code) => {
    setPicked((prev) => (prev.includes(code)
      ? prev.filter((c) => c !== code)
      : [...prev, code]));
  };

  const submit = async () => {
    const clean = name.trim();
    if (!clean) { setError('Название папки обязательно'); return; }
    const payload = { name: clean, emoji, auto_rules: picked };
    const res = editing ? await onUpdate(editing.id, payload) : await onCreate(payload);
    if (res?.status === 'ok') setMode('list');
    else setError(res?.error || 'Не удалось сохранить папку');
  };

  const chatFolders = useMemo(() => {
    if (!pickChat) return new Set();
    return new Set(
      folders
        .filter((f) => (f.manual_chat_ids || []).some((id) => String(id) === String(pickChat.chat_id)))
        .map((f) => f.id),
    );
  }, [folders, pickChat]);

  const Header = ({ title, onBack }) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
      {onBack && (
        <IconButton
          size="sm" variant="plain" onClick={onBack}
          sx={(theme) => ({
            borderRadius: '50%',
            ...iconColor(theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.72)'),
          })}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      <Typography
        level="title-sm"
        startDecorator={!onBack ? <FolderRoundedIcon sx={{ fontSize: 18 }} /> : null}
        sx={{ fontWeight: 800, flex: 1 }}
      >
        {title}
      </Typography>
      <IconButton
        size="sm" variant="plain" onClick={onClose}
        sx={(theme) => ({
          borderRadius: '50%',
          ...iconColor(theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.72)'),
        })}
      >
        <CloseRoundedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Stack>
  );

  const slide = {
    initial: { opacity: 0, x: 24 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -24 },
    transition: { duration: MOTION.base, ease: MOTION.ease },
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 12100 }}>
      {/* ModalOverflow + чистый ModalDialog. Анимация переехала внутрь
          намеренно: с component={motion.div} на самом диалоге framer пишет
          свой transform и затирает translate(-50%,-50%), которым Joy центрует
          окно, — окно уезжало из центра, а на телефоне почти за экран. */}
      <ModalOverflow sx={{ p: { xs: 1.5, sm: 2 } }}>
      <ModalDialog
        sx={{
          width: 420, maxWidth: '94vw', borderRadius: '18px', p: 2,
          overflow: 'hidden',
          position: 'relative', top: 'auto', left: 'auto', my: 'auto',
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: MOTION.base, ease: MOTION.ease }}
        >
        <AnimatePresence mode="wait">
          {mode === 'list' && (
            <Box key="list" component={motion.div} {...slide}>
              <Header title="Папки чатов" />

              <Box sx={(theme) => ({ maxHeight: '52vh', overflowY: 'auto', ...thinScrollbar(theme) })}>
                {folders.length === 0 && (
                  <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center', py: 3 }}>
                    Папок пока нет. Создайте первую — и чаты перестанут лежать одной кучей.
                  </Typography>
                )}

                {folders.map((f) => (
                  <Box
                    key={f.id}
                    component={motion.div}
                    layout
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1, py: 0.875, borderRadius: '12px',
                      '&:hover': { backgroundColor: 'background.level1' },
                    }}
                  >
                    <Box sx={{ fontSize: 17, width: 24, textAlign: 'center' }}>
                      {f.emoji || '📁'}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography level="body-sm" sx={{ ...ellipsis, fontWeight: 650 }}>
                        {f.name}
                      </Typography>
                      <Typography level="body-xs" sx={{ ...ellipsis, color: 'text.tertiary' }}>
                        {f.chats_count} чат(ов)
                        {(f.auto_rules || []).length > 0 && ' · умная'}
                      </Typography>
                    </Box>
                    <IconButton
                      size="sm" variant="plain" onClick={() => startEdit(f)} title="Изменить"
                      sx={(theme) => ({
                        borderRadius: '50%',
                        ...iconColor('var(--joy-palette-text-tertiary)'),
                      })}
                    >
                      <EditRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                    <IconButton
                      size="sm" variant="plain" color="danger"
                      // Спрашиваем: папку легко снести случайно, а правила
                      // автоподбора потом набирать заново.
                      onClick={() => {
                        const ok = typeof window === 'undefined'
                          || window.confirm(`Удалить папку «${f.name}»? Сами чаты останутся на месте.`);
                        if (ok) onDelete(f.id);
                      }}
                      title="Удалить папку"
                      sx={{ borderRadius: '50%', ...iconColor('var(--joy-palette-danger-400)') }}
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Button
                fullWidth
                variant="soft"
                startDecorator={<AddRoundedIcon />}
                onClick={startCreate}
                sx={{ mt: 1.5, borderRadius: '12px', fontWeight: 700 }}
              >
                Новая папка
              </Button>
            </Box>
          )}

          {mode === 'form' && (
            <Box key="form" component={motion.div} {...slide}>
              <Header
                title={editing ? 'Изменить папку' : 'Новая папка'}
                onBack={() => setMode('list')}
              />

              <Stack spacing={1.5}>
                <Box>
                  <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                    Название
                  </Typography>
                  <Input
                    size="sm"
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 64))}
                    placeholder="Например: Обмерщики"
                    sx={{ borderRadius: '12px' }}
                  />
                </Box>

                <Box>
                  <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                    Значок
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {EMOJI_SET.map((em) => (
                      <Box
                        key={em || 'none'}
                        component={motion.button}
                        type="button"
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setEmoji(em)}
                        sx={{
                          width: 32, height: 32, borderRadius: '10px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 16, cursor: 'pointer',
                          border: '1px solid',
                          borderColor: emoji === em ? 'primary.400' : 'divider',
                          backgroundColor: emoji === em ? 'rgba(99,102,241,0.14)' : 'transparent',
                        }}
                      >
                        {em || '—'}
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography level="body-xs" sx={{ mb: 0.25, color: 'text.tertiary' }}>
                    Собирать автоматически
                  </Typography>
                  <Typography level="body-xs" sx={{ mb: 0.75, color: 'text.tertiary', opacity: 0.8 }}>
                    Чаты подбираются на лету по выбранным признакам. Можно ничего
                    не отмечать и складывать чаты руками. Выбранный тип ограничивает состав папки, в том числе при ручном добавлении.
                  </Typography>
                  <Box sx={(theme) => ({ maxHeight: '32vh', overflowY: 'auto', ...thinScrollbar(theme) })}>
                    {rules.map((r) => (
                      <Checkbox
                        key={r.code}
                        size="sm"
                        label={r.label}
                        checked={picked.includes(r.code)}
                        onChange={() => toggleRule(r.code)}
                        sx={{ display: 'flex', py: 0.4 }}
                      />
                    ))}
                  </Box>
                </Box>

                {error && (
                  <Typography level="body-sm" color="danger">{error}</Typography>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="soft" color="neutral" onClick={() => setMode('list')}
                    sx={{ borderRadius: '12px', flex: 1 }}
                  >
                    Отмена
                  </Button>
                  <Button
                    variant="solid" loading={busy} onClick={submit}
                    sx={{ borderRadius: '12px', flex: 2, fontWeight: 700 }}
                  >
                    {editing ? 'Сохранить' : 'Создать'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {mode === 'pick' && (
            <Box key="pick" component={motion.div} {...slide}>
              <Header title="Добавить в папку" />

              <Typography level="body-xs" sx={{ ...ellipsis, color: 'text.tertiary', mb: 1 }}>
                {pickChat?.display_name || pickChat?.name || `Чат №${pickChat?.chat_id}`}
              </Typography>

              <Box sx={(theme) => ({ maxHeight: '52vh', overflowY: 'auto', ...thinScrollbar(theme) })}>
                {folders.length === 0 && (
                  <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center', py: 3 }}>
                    Папок пока нет — создайте первую.
                  </Typography>
                )}

                {folders.map((f) => {
                  const inside = chatFolders.has(f.id);
                  return (
                    <Box
                      key={f.id}
                      onClick={() => onToggleChat(f.id, pickChat.chat_id, inside ? 'remove' : 'add')}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1, py: 0.875, borderRadius: '12px', cursor: 'pointer',
                        '&:hover': { backgroundColor: 'background.level1' },
                      }}
                    >
                      <Box sx={{ fontSize: 17, width: 24, textAlign: 'center' }}>
                        {f.emoji || '📁'}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="body-sm" sx={{ ...ellipsis, fontWeight: 650 }}>
                          {f.name}
                        </Typography>
                        {(f.auto_rules || []).length > 0 && (
                          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                            умная папка
                          </Typography>
                        )}
                      </Box>
                      {inside && (
                        <CheckRoundedIcon sx={{ fontSize: 18, color: 'primary.500' }} />
                      )}
                    </Box>
                  );
                })}
              </Box>

              <Button
                fullWidth
                variant="soft"
                startDecorator={<AddRoundedIcon />}
                onClick={startCreate}
                sx={{ mt: 1.5, borderRadius: '12px', fontWeight: 700 }}
              >
                Новая папка
              </Button>
            </Box>
          )}
        </AnimatePresence>

        {busy && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                     backgroundColor: 'rgba(255,255,255,0.35)', pointerEvents: 'none' }}>
            <CircularProgress size="sm" />
          </Box>
        )}
        </Box>
      </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}
